
import { useState } from 'react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Mail, AlertCircle, Loader2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useDemoMode } from '@/hooks/useDemoMode';
import { resendVerificationEmail } from '@/services/auth.service';
import { toast } from 'sonner';

export const EmailVerificationBanner = () => {
  const { user, isEmailVerified, session } = useAuth();
  const { isEmailVerifiedOverride } = useDemoMode();
  const [isResending, setIsResending] = useState(false);

  // Check if user authenticated via phone (phone users don't need email verification)
  const isPhoneUser = session?.user?.phone && !session?.user?.email;

  // Hide banner if email is verified OR if demo user override is active OR if user authenticated via phone
  if (isEmailVerified || isEmailVerifiedOverride || !user || isPhoneUser) {
    return null;
  }

  const handleResendEmail = async () => {
    if (!user?.email || !user?.id) return;
    
    setIsResending(true);
    try {
      await resendVerificationEmail(user.email, user.id);
      toast.success('Verification email sent! Please check your inbox.');
    } catch (error: any) {
      toast.error(error.message || 'Failed to send verification email');
    } finally {
      setIsResending(false);
    }
  };

  return (
    <Alert className="border-orange-200 bg-orange-50">
      <AlertCircle className="h-4 w-4 text-orange-600" />
      <AlertDescription className="text-orange-800">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Mail className="h-4 w-4" />
            <span>
              Please verify your email address to access your dashboard. Check your inbox for a verification email.
            </span>
          </div>
          <div className="flex gap-2 ml-4">
            <Button
              variant="outline"
              size="sm"
              onClick={handleResendEmail}
              disabled={isResending}
              className="text-orange-700 border-orange-300"
            >
              {isResending ? (
                <>
                  <Loader2 className="h-3 w-3 animate-spin mr-1" />
                  Sending...
                </>
              ) : (
                'Resend email'
              )}
            </Button>
          </div>
        </div>
      </AlertDescription>
    </Alert>
  );
};
